import {gql} from '@apollo/client';

import {assertAndContinue} from 'Asserts';
import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  CustomResolutionQueryType,
  ServerResolutionResult,
  StringResolutionType,
  TransferTableSourceEntityValidateIsContainableQuery,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {BaseTypedValueType, CustomEvalResult, TypedValue} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
  ResolveTypedValueToTypeFn,
} from 'notebook/tables/typed-values/TypedValueResolution';

import {
  GetBioentityIsContainable,
  GetBioentityIsContainableVariables,
} from './__generated/GetBioentityIsContainable';

const GET_BIOENTITY_IS_CONTAINABLE_QUERY = gql`
  query GetBioentityIsContainable($bioentityId: StrictString!) @skipAudit @engComponent(name: INVENTORY) {
    bioentity(id: $bioentityId) {
      ... on RegistryItemInterface {
        id
        tagSchema {
          id
          name
          isContainable
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// To mock our GraphQL call, we need to wrap it in a method.
// This is different from most of our GraphQL tests, where we mock the Apollo cache.
// Here, mocking the cache won't work, because our fetch policy is `'no-cache'`.
export const stubbableFunctions = {
  async doQuery(bioentityId: string): Promise<GetBioentityIsContainable> {
    return await doQueryWithoutCache<GetBioentityIsContainable, GetBioentityIsContainableVariables>(
      {query: GET_BIOENTITY_IS_CONTAINABLE_QUERY},
      {variables: {bioentityId}}
    );
  },
};

export const SourceEntityResolver = {
  validateSourceEntityIsContainable: async (
    query: TransferTableSourceEntityValidateIsContainableQuery
  ): Promise<ServerResolutionResult> => {
    const {bioentityId} = query;

    const data = await stubbableFunctions.doQuery(bioentityId);

    const bioentity = data.bioentity;
    if (isInaccessible(bioentity)) {
      // TODO(BNCH-15696): inaccessible chips will be marked invalid after customEval
      return {
        type: StringResolutionType.CUSTOM,
        error: undefined,
        resolvedValue: {
          typedValue: null,
        },
      };
    }

    if (!bioentity.tagSchema || !bioentity.tagSchema.isContainable) {
      return {
        type: undefined,
        error: 'Entity is not configured for inventory',
        resolvedValue: undefined,
      };
    }

    return {
      type: StringResolutionType.CUSTOM,
      error: undefined,
      resolvedValue: {
        typedValue: null,
      },
    };
  },
};

export function sourceEntityCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: [],
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn,
  resolveTypedValueToType: ResolveTypedValueToTypeFn
): CustomEvalResult {
  // TODO(BNCH-15744): customEval currently runs _before_ string resolution and this particular resolution
  // would like to operate on a post-resolved value, so we manually fire it off first.
  const {resolvedValue: resolvedTypedValue, error: resolutionError} = resolveTypedValueToType(
    columnDefinition,
    typedValue
  );
  if (resolutionError) {
    return {val: resolvedTypedValue, error: resolutionError, locked: false};
  }

  const bioentityInfo =
    resolvedTypedValue &&
    resolvedTypedValue.type === BaseTypedValueType.BIOENTITY &&
    resolvedTypedValue.bioentitySummary;
  if (!bioentityInfo) {
    // resolveTypedValue should have caught this, and we should have taken the early `return` above.
    // This column should be marked `isRequired`, and resolveTypedValue should error
    // when a required column is missing a value.
    assertAndContinue(false, 'Missing bioentity should have been caught by resolveTypedValue');
    return {val: resolvedTypedValue, error: null, locked: false};
  }

  const {resolvedValue, error} = resolveFromResolutionQuery(
    resolvedTypedValue,
    {
      customResolutionQueryType:
        CustomResolutionQueryType.TRANSFER_TABLE_SOURCE_ENTITY_VALIDATE_IS_CONTAINABLE,
      bioentityId: bioentityInfo.id,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
